/**
 * Parking — EV chargers (/parking/chargers) — V0.3 marquee Tier 1.
 *
 * Society + owner-installed EV chargers. KPI strip + filters + table + add/edit
 * dialog + per-row photo upload + "Install requests" pill linking to requests.
 *
 * Backend contracts:
 *   GET    /v1/parking/chargers?location_kind&ownership_kind&zone_id&status&cursor&limit
 *   POST   /v1/parking/chargers
 *   PATCH  /v1/parking/chargers/:id
 *   DELETE /v1/parking/chargers/:id
 *   POST   /v1/parking/chargers/:id/photo               multipart
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  ArrowLeft,
  Zap,
  Plus,
  Edit3,
  Trash2,
  Filter,
  Eye,
  Upload,
  Wrench,
  Wifi,
  IndianRupee
} from 'lucide-react';
import { toast } from 'sonner';
import { get, post, patch, del, getErrorMessage, api } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { CursorPaginator } from '@/components/ui/cursor-paginator';
import { useCursorAccumulator } from '@/hooks/useCursorAccumulator';
import type {
  EvCharger,
  ChargerLocation,
  ChargerOwnership,
  ChargerStatus,
  ConnectorType,
  ParkingZone,
  UnitLite,
  CursorList
} from './_parking-types';
import { CHARGER_STATUS_LABEL, CHARGER_STATUS_BADGE } from './_parking-types';

const LOCATION_LABEL: Record<ChargerLocation, string> = {
  in_parking_slot: 'In parking slot',
  common_area: 'Common area'
};
const OWNERSHIP_LABEL: Record<ChargerOwnership, string> = {
  society_installed: 'Society',
  owner_installed: 'Owner'
};
const CONNECTOR_LABEL: Record<ConnectorType, string> = {
  type1: 'Type-1',
  type2: 'Type-2',
  ccs2: 'CCS-2',
  chademo: 'CHAdeMO',
  gbt: 'GB/T',
  three_pin: '3-pin'
};

const CHARGER_STATUS_VALUES: ChargerStatus[] = ['active', 'broken', 'maintenance', 'retired'];

function formatInr(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `₹${n.toLocaleString('en-IN')}`;
}

const ChargerSchema = z.object({
  zone_id: z.string().optional().or(z.literal('')),
  slot_id: z.string().optional().or(z.literal('')),
  location_kind: z.enum(['in_parking_slot', 'common_area']),
  location_label: z.string().trim().max(120).optional().or(z.literal('')),
  ownership_kind: z.enum(['society_installed', 'owner_installed']),
  owner_unit_id: z.string().optional().or(z.literal('')),
  brand: z.string().trim().max(80).optional().or(z.literal('')),
  model: z.string().trim().max(80).optional().or(z.literal('')),
  power_kw: z.coerce.number().min(0).max(1000),
  connector_type: z.enum(['type1', 'type2', 'ccs2', 'chademo', 'gbt', 'three_pin']),
  kwh_rate_inr: z.coerce.number().min(0).max(1000).optional(),
  status: z.enum(['active', 'broken', 'maintenance', 'retired']).default('active'),
  serial_no: z.string().trim().max(80).optional().or(z.literal('')),
  installed_at: z.string().optional().or(z.literal('')),
  notes: z.string().trim().max(500).optional().or(z.literal(''))
});
type ChargerForm = z.infer<typeof ChargerSchema>;

const DEFAULT_CHARGER_FORM: ChargerForm = {
  zone_id: '',
  slot_id: '',
  location_kind: 'in_parking_slot',
  location_label: '',
  ownership_kind: 'society_installed',
  owner_unit_id: '',
  brand: '',
  model: '',
  power_kw: 7,
  connector_type: 'type2',
  kwh_rate_inr: 12,
  status: 'active',
  serial_no: '',
  installed_at: '',
  notes: ''
};

export function ParkingChargersPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [locationFilter, setLocationFilter] = useState<string>('');
  const [ownershipFilter, setOwnershipFilter] = useState<string>('');
  const [zoneFilter, setZoneFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  const filtersHash = useMemo(
    () => JSON.stringify([locationFilter, ownershipFilter, zoneFilter, statusFilter]),
    [locationFilter, ownershipFilter, zoneFilter, statusFilter]
  );
  const [cursor, setCursor] = useState<string | null>(null);
  useEffect(() => {
    setCursor(null);
  }, [filtersHash]);

  const listQ = useQuery({
    queryKey: ['parking-chargers', filtersHash, cursor],
    queryFn: () =>
      get<CursorList<EvCharger>>('/parking/chargers', {
        params: {
          limit: 50,
          ...(locationFilter ? { location_kind: locationFilter } : {}),
          ...(ownershipFilter ? { ownership_kind: ownershipFilter } : {}),
          ...(zoneFilter ? { zone_id: zoneFilter } : {}),
          ...(statusFilter ? { status: statusFilter } : {}),
          ...(cursor ? { cursor } : {})
        }
      })
  });
  const paginator = useCursorAccumulator<EvCharger>(listQ.data, cursor, filtersHash);
  const chargers = paginator.items;

  const zonesQ = useQuery({
    queryKey: ['parking-zones'],
    queryFn: () => get<CursorList<ParkingZone>>('/parking/zones', { params: { limit: 200 } }),
    staleTime: 5 * 60 * 1000
  });
  const zones = zonesQ.data?.items ?? [];

  // Pending requests count for the badge.
  const pendingReqQ = useQuery({
    queryKey: ['parking-charger-requests', 'pending'],
    queryFn: () =>
      get<{ items: unknown[] }>('/parking/charger-requests', {
        params: { status: 'pending', limit: 1 }
      }),
    staleTime: 60_000
  });
  const pendingReqCount = pendingReqQ.data?.items.length ?? 0;

  // KPI strip — counts from accumulated rows.
  const totalChargers = chargers.length;
  const activeCount = chargers.filter((c) => c.status === 'active').length;
  const brokenCount = chargers.filter((c) => c.status === 'broken').length;
  const societyCount = chargers.filter((c) => c.ownership_kind === 'society_installed').length;
  const ownerCount = chargers.filter((c) => c.ownership_kind === 'owner_installed').length;
  const totalRevenue = chargers.reduce((s, c) => s + (c.total_revenue_inr ?? 0), 0);

  // Create / edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<EvCharger | null>(null);
  const form = useForm<ChargerForm>({
    resolver: zodResolver(ChargerSchema),
    defaultValues: DEFAULT_CHARGER_FORM,
    mode: 'onChange'
  });
  const locationKind = form.watch('location_kind') as ChargerLocation;
  const ownershipKind = form.watch('ownership_kind') as ChargerOwnership;

  // Units list (for owner-unit picker)
  const unitsQ = useQuery({
    queryKey: ['units-lite'],
    queryFn: () => get<{ items: UnitLite[] }>('/society/units', { params: { limit: 1000 } }),
    enabled: dialogOpen && ownershipKind === 'owner_installed',
    staleTime: 5 * 60 * 1000
  });

  const openCreate = (): void => {
    setEditing(null);
    form.reset(DEFAULT_CHARGER_FORM);
    setDialogOpen(true);
  };
  const openEdit = (c: EvCharger): void => {
    setEditing(c);
    form.reset({
      zone_id: c.zone_id ?? '',
      slot_id: c.slot_id ?? '',
      location_kind: c.location_kind,
      location_label: c.location_label ?? '',
      ownership_kind: c.ownership_kind,
      owner_unit_id: c.owner_unit_id ?? '',
      brand: c.brand ?? '',
      model: c.model ?? '',
      power_kw: c.power_kw,
      connector_type: c.connector_type,
      kwh_rate_inr: c.kwh_rate_inr ?? 0,
      status: c.status,
      serial_no: c.serial_no ?? '',
      installed_at: c.installed_at ? c.installed_at.slice(0, 10) : '',
      notes: c.notes ?? ''
    });
    setDialogOpen(true);
  };

  const save = useMutation({
    mutationFn: (v: ChargerForm) => {
      const body = {
        zone_id: v.zone_id?.trim() || null,
        slot_id: v.slot_id?.trim() || null,
        location_kind: v.location_kind,
        location_label: v.location_label?.trim() || null,
        ownership_kind: v.ownership_kind,
        owner_unit_id:
          v.ownership_kind === 'owner_installed' ? v.owner_unit_id?.trim() || null : null,
        brand: v.brand?.trim() || null,
        model: v.model?.trim() || null,
        power_kw: v.power_kw,
        connector_type: v.connector_type,
        kwh_rate_inr: v.kwh_rate_inr ?? null,
        status: v.status,
        serial_no: v.serial_no?.trim() || null,
        installed_at: v.installed_at || null,
        notes: v.notes?.trim() || null
      };
      if (editing) return patch<EvCharger>(`/parking/chargers/${editing.id}`, body);
      return post<EvCharger>('/parking/chargers', body);
    },
    onSuccess: () => {
      toast.success(editing ? 'Charger updated' : 'Charger created');
      setDialogOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['parking-chargers'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const [deleteTarget, setDeleteTarget] = useState<EvCharger | null>(null);
  const remove = useMutation({
    mutationFn: (id: string) => del<unknown>(`/parking/chargers/${id}`),
    onSuccess: () => {
      toast.success('Charger deleted');
      setDeleteTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['parking-chargers'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const uploadPhoto = useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }) => {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/parking/chargers/${id}/photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return res.data;
    },
    onSuccess: () => {
      toast.success('Photo uploaded');
      void queryClient.invalidateQueries({ queryKey: ['parking-chargers'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  return (
    <>
      <PageHeader
        title="EV chargers"
        description="Society + owner-installed chargers, rates, sessions, and revenue."
        actions={
          <div className="flex items-center gap-vt-2">
            <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/parking' })} title="Back to parking">
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate({ to: '/parking/charger-requests' })}
            >
              <Wrench className="h-4 w-4" /> Install requests
              {pendingReqCount > 0 ? (
                <span className="ml-vt-1 rounded-vt-pill bg-vt-warn-100 px-1.5 py-0 text-[10px] font-semibold text-vt-warn">
                  {pendingReqCount}
                </span>
              ) : null}
            </Button>
            <Button size="sm" onClick={openCreate}>
              <Plus className="h-4 w-4" /> Add charger
            </Button>
          </div>
        }
      />

      <div className="mx-auto w-full max-w-[1400px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
        {/* KPI strip */}
        <div className="grid grid-cols-2 gap-vt-4 md:grid-cols-3 xl:grid-cols-6">
          <Kpi icon={Zap} label="Total" value={totalChargers} loading={listQ.isLoading} tone="primary" />
          <Kpi icon={Wifi} label="Active" value={activeCount} loading={listQ.isLoading} tone="safe" />
          <Kpi icon={Wrench} label="Broken" value={brokenCount} loading={listQ.isLoading} tone="alert" />
          <Kpi icon={Zap} label="Society" value={societyCount} loading={listQ.isLoading} tone="info" />
          <Kpi icon={Zap} label="Owner" value={ownerCount} loading={listQ.isLoading} tone="warn" />
          <Kpi icon={IndianRupee} label="Revenue" value={formatInr(totalRevenue)} loading={listQ.isLoading} tone="warn" isText />
        </div>

        {/* Filters + table */}
        <Card>
          <CardContent className="p-vt-4">
            <div className="mb-vt-3 flex flex-wrap items-center gap-vt-3">
              <Filter className="h-4 w-4 text-vt-text-muted" />
              <div className="flex-1 min-w-[160px]">
                <Select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}>
                  <option value="">All locations</option>
                  {(Object.keys(LOCATION_LABEL) as ChargerLocation[]).map((k) => (
                    <option key={k} value={k}>
                      {LOCATION_LABEL[k]}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex-1 min-w-[160px]">
                <Select value={ownershipFilter} onChange={(e) => setOwnershipFilter(e.target.value)}>
                  <option value="">All ownership</option>
                  {(Object.keys(OWNERSHIP_LABEL) as ChargerOwnership[]).map((k) => (
                    <option key={k} value={k}>
                      {OWNERSHIP_LABEL[k]}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex-1 min-w-[160px]">
                <Select value={zoneFilter} onChange={(e) => setZoneFilter(e.target.value)}>
                  <option value="">All zones</option>
                  {zones.map((z) => (
                    <option key={z.id} value={z.id}>
                      {z.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex-1 min-w-[140px]">
                <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="">All statuses</option>
                  {CHARGER_STATUS_VALUES.map((k) => (
                    <option key={k} value={k}>
                      {CHARGER_STATUS_LABEL[k]}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            {listQ.isLoading ? (
              <div className="flex flex-col gap-vt-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : listQ.isError ? (
              <EmptyState
                icon={<Zap className="h-6 w-6" />}
                title="Could not load chargers"
                description={getErrorMessage(listQ.error)}
              />
            ) : chargers.length === 0 ? (
              <EmptyState
                icon={<Zap className="h-6 w-6" />}
                title="No chargers yet"
                description="Add society or owner-installed EV chargers to start tracking sessions and revenue."
                action={
                  <Button onClick={openCreate}>
                    <Plus className="h-4 w-4" /> Add charger
                  </Button>
                }
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Location</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">kW</TableHead>
                    <TableHead>Connector</TableHead>
                    <TableHead>Brand</TableHead>
                    <TableHead>Ownership</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {chargers.map((c) => (
                    <TableRow
                      key={c.id}
                      className="cursor-pointer hover:bg-vt-surface-2"
                      onClick={() => navigate({ to: '/parking/chargers/$id', params: { id: c.id } })}
                    >
                      <TableCell className="font-medium text-vt-text">
                        {c.location_label ?? c.slot?.slot_number ?? c.zone?.name ?? '—'}
                      </TableCell>
                      <TableCell className="text-vt-13 text-vt-text-muted">
                        {LOCATION_LABEL[c.location_kind]}
                      </TableCell>
                      <TableCell className="text-right text-vt-text-muted">{c.power_kw}</TableCell>
                      <TableCell className="text-vt-13 text-vt-text-muted">
                        {CONNECTOR_LABEL[c.connector_type]}
                      </TableCell>
                      <TableCell className="text-vt-13 text-vt-text-muted">
                        {c.brand ?? '—'}
                        {c.model ? ` ${c.model}` : ''}
                      </TableCell>
                      <TableCell className="text-vt-13 text-vt-text-muted">
                        {OWNERSHIP_LABEL[c.ownership_kind]}
                      </TableCell>
                      <TableCell>
                        <Badge variant={CHARGER_STATUS_BADGE[c.status]}>
                          {CHARGER_STATUS_LABEL[c.status]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono text-vt-13 text-vt-text">
                        {formatInr(c.total_revenue_inr)}
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="inline-flex items-center gap-vt-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            title="View detail"
                            onClick={() => navigate({ to: '/parking/chargers/$id', params: { id: c.id } })}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" title="Edit" onClick={() => openEdit(c)}>
                            <Edit3 className="h-4 w-4" />
                          </Button>
                          <label className="inline-flex">
                            <input
                              type="file"
                              accept="image/png,image/jpeg,image/webp"
                              className="hidden"
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) uploadPhoto.mutate({ id: c.id, file: f });
                                e.target.value = '';
                              }}
                            />
                            <Button size="sm" variant="ghost" asChild title="Upload photo">
                              <span>
                                <Upload className="h-4 w-4" />
                              </span>
                            </Button>
                          </label>
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Delete"
                            onClick={() => setDeleteTarget(c)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <CursorPaginator
              hasMore={Boolean(paginator.nextCursor)}
              loading={listQ.isFetching}
              itemsShown={chargers.length}
              pageSize={50}
              onLoadMore={() => {
                if (paginator.nextCursor) setCursor(paginator.nextCursor);
              }}
            />
          </CardContent>
        </Card>
      </div>

      {/* Create / edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit charger' : 'Add EV charger'}</DialogTitle>
            <DialogDescription>
              Society + owner-installed chargers tracked alongside parking slots.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={form.handleSubmit((v) => save.mutate(v))}
            className="flex flex-col gap-vt-3"
          >
            <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="c-loc">Location kind</Label>
                <Select id="c-loc" {...form.register('location_kind')}>
                  {(Object.keys(LOCATION_LABEL) as ChargerLocation[]).map((k) => (
                    <option key={k} value={k}>
                      {LOCATION_LABEL[k]}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="c-zone">Zone</Label>
                <Select id="c-zone" {...form.register('zone_id')}>
                  <option value="">— Optional —</option>
                  {zones.map((z) => (
                    <option key={z.id} value={z.id}>
                      {z.name}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="c-label">Location label</Label>
              <Input
                id="c-label"
                placeholder={locationKind === 'in_parking_slot' ? 'B1-014' : 'Lobby wall'}
                {...form.register('location_label')}
              />
            </div>
            <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="c-own">Ownership</Label>
                <Select id="c-own" {...form.register('ownership_kind')}>
                  {(Object.keys(OWNERSHIP_LABEL) as ChargerOwnership[]).map((k) => (
                    <option key={k} value={k}>
                      {OWNERSHIP_LABEL[k]}
                    </option>
                  ))}
                </Select>
              </div>
              {ownershipKind === 'owner_installed' ? (
                <div className="flex flex-col gap-vt-1">
                  <Label htmlFor="c-owner-unit">Owner unit</Label>
                  <Select id="c-owner-unit" {...form.register('owner_unit_id')}>
                    <option value="">— Pick unit —</option>
                    {(unitsQ.data?.items ?? []).map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.block ? `${u.block.name} · ` : ''}
                        {u.number}
                      </option>
                    ))}
                  </Select>
                </div>
              ) : null}
            </div>
            <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-3">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="c-brand">Brand</Label>
                <Input id="c-brand" placeholder="Tata Power" {...form.register('brand')} />
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="c-model">Model</Label>
                <Input id="c-model" {...form.register('model')} />
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="c-serial">Serial #</Label>
                <Input id="c-serial" {...form.register('serial_no')} />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-3">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="c-kw">Power (kW)</Label>
                <Input id="c-kw" type="number" min={0} step="0.1" {...form.register('power_kw')} />
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="c-conn">Connector</Label>
                <Select id="c-conn" {...form.register('connector_type')}>
                  {(Object.keys(CONNECTOR_LABEL) as ConnectorType[]).map((k) => (
                    <option key={k} value={k}>
                      {CONNECTOR_LABEL[k]}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="c-rate">Rate (₹/kWh)</Label>
                <Input
                  id="c-rate"
                  type="number"
                  min={0}
                  step="0.01"
                  {...form.register('kwh_rate_inr')}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="c-status">Status</Label>
                <Select id="c-status" {...form.register('status')}>
                  {CHARGER_STATUS_VALUES.map((k) => (
                    <option key={k} value={k}>
                      {CHARGER_STATUS_LABEL[k]}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="c-installed">Installed on</Label>
                <Input id="c-installed" type="date" {...form.register('installed_at')} />
              </div>
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="c-notes">Notes</Label>
              <Textarea id="c-notes" rows={2} {...form.register('notes')} />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={save.isPending}
                loadingText="Saving…"
                disabled={save.isPending || !form.formState.isValid}
              >
                {editing ? 'Save changes' : 'Create charger'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(v) => (v ? null : setDeleteTarget(null))}
        title="Delete charger"
        description={
          deleteTarget ? (
            <>
              Permanently delete charger at <strong>{deleteTarget.location_label ?? '—'}</strong>?
              Only allowed when no sessions reference it.
            </>
          ) : null
        }
        confirmLabel="Delete"
        variant="danger"
        typeToConfirm="DELETE"
        loading={remove.isPending}
        onConfirm={() => {
          if (deleteTarget) remove.mutate(deleteTarget.id);
        }}
      />
    </>
  );
}

// ───── KPI card ─────
interface KpiProps {
  icon: typeof Zap;
  label: string;
  value: number | string;
  loading?: boolean;
  tone?: 'primary' | 'info' | 'safe' | 'warn' | 'alert' | 'muted';
  isText?: boolean;
}
function Kpi({ icon: Icon, label, value, loading, tone = 'primary', isText = false }: KpiProps): JSX.Element {
  const toneClass: Record<NonNullable<KpiProps['tone']>, string> = {
    primary: 'bg-vt-primary-100 text-vt-primary',
    info: 'bg-[color:var(--vt-info-100)] text-vt-info',
    safe: 'bg-[color:var(--vt-safe-100)] text-[color:var(--vt-safe)]',
    warn: 'bg-[color:var(--vt-warn-100)] text-vt-warn',
    alert: 'bg-[color:var(--vt-alert-100)] text-vt-alert',
    muted: 'bg-vt-surface-2 text-vt-text-muted'
  };
  return (
    <Card>
      <CardContent className="flex items-center gap-vt-3 p-vt-4">
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md ${toneClass[tone]}`}>
          <Icon className="h-5 w-5" strokeWidth={1.75} />
        </span>
        <div>
          <div className="text-vt-12 text-vt-text-muted">{label}</div>
          <div className="text-vt-20 font-semibold text-vt-text">
            {loading ? <Skeleton className="h-7 w-12" /> : isText ? <span>{value}</span> : <span>{value}</span>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
